import React, { useState, useEffect, useMemo, useRef } from "react"
import ReactDOM from "react-dom"
import { Info } from "lucide-react"

export default function PopupPracticeSelector({
  selectOptions,
  selectedValues,
  onChange,
  onApply,
  linkLabel = "Select Options",
  title = "Select Items",
  saveLoading,
  onlySubfolderAssociation = null,
  type = "",
}) {
  const [allOptions, setAllOptions] = useState([])
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [localSelected, setLocalSelected] = useState([])
  const [loading] = useState(false)
  const [onlySubfolderAssociationState, setOnlySubfolderAssociationState] =
    useState(false)

  const hasOpenedOnce = useRef(false)
  const flgDiff = areArraysDifferent(selectedValues, localSelected)

  const fetchUsersByFaxNumber = async () => {
    setAllOptions(selectOptions)
  }

  // Sync local state when selectedValues prop changes
  useEffect(() => {
    setLocalSelected(selectedValues)
  }, [selectedValues])

  useEffect(() => {
    setOnlySubfolderAssociationState(onlySubfolderAssociation?.selectedValue)

    // Track that the modal was opened at least once
    if (open) {
      hasOpenedOnce.current = true
      fetchUsersByFaxNumber()
      return
    }

    // If modal is closing, and it was opened before
    if (!open && hasOpenedOnce.current && flgDiff) {
      const confirmClose = window.confirm(
        "You have unsaved changes. Are you sure you want to close?",
      )

      if (!confirmClose) {
        setOpen(true) // Reopen the modal
      } else {
        setLocalSelected(selectedValues) // Reset the local state
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Sort asc by label
  const sortedOptions = useMemo(
    () => [...allOptions].sort((a, b) => a.label.localeCompare(b.label)),
    [allOptions],
  )

  // Filter by search
  const filteredOptions = useMemo(
    () =>
      sortedOptions.filter((opt) =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [sortedOptions, searchTerm],
  )

  const isAllSelected = localSelected.length === allOptions.length

  function areArraysDifferent(arr1, arr2) {
    const serialize = (arr) =>
      arr
        .map((item) => `${item.value}-${item.label}`)
        .sort()
        .join("|")

    return serialize(arr1) !== serialize(arr2)
  }

  // Toggle a single option and notify via onChange
  const toggleOption = (option) => {
    const exists = localSelected.some((o) => o.value === option.value)
    const newSelected = exists
      ? localSelected.filter((o) => o.value !== option.value)
      : [...localSelected, option]
    setLocalSelected(newSelected)
    if (onChange) onChange(newSelected)
  }

  // Select or clear all and notify
  const handleSelectAll = () => {
    const newSelected = isAllSelected ? [] : [...allOptions]
    setLocalSelected(newSelected)
    if (onChange) onChange(newSelected)
  }

  // Apply changes: call onApply if provided
  const applyChanges = () => {
    if (onApply) onApply(localSelected, onlySubfolderAssociationState)
  }

  return (
    <>
      <button
        className="faxes-button open-button"
        onClick={() => setOpen(true)}
      >
        {linkLabel}
      </button>

      {open &&
        ReactDOM.createPortal(
          <>
            <div
              className="checkbox-popup-overlay"
              onClick={() => setOpen(false)}
            />

            <div className="checkbox-popup-modal">
              <div className="checkbox-popup-header">
                <h3 className="popup-title">{title}</h3>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  style={{
                    background: "transparent",
                    border: "none",
                    fontSize: 24,
                    cursor: "pointer",
                  }}
                >
                  &times;
                </button>
              </div>

              <div className="checkbox-popup-body">
                {selectOptions.length > 0 && (
                  <div
                    className="search-container"
                    style={{
                      justifyContent: "space-between",
                      padding: "5px",
                      marginBottom: "10px",
                    }}
                  >
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ width: "500px" }}
                    />

                    {filteredOptions.length > 0 && (
                      <button
                        type="button"
                        className="select-all-button"
                        onClick={handleSelectAll}
                      >
                        {isAllSelected ? "Deselect All" : "Select All"}
                      </button>
                    )}
                  </div>
                )}

                {loading ? (
                  <div className="loading-container-fax-list">
                    <div className="loading-message">Loading...</div>
                  </div>
                ) : (
                  <>
                    {filteredOptions.length > 0 ? (
                      <div className="checkbox-grid">
                        {filteredOptions.map((option) => {
                          const checked = localSelected.some(
                            (o) => o.value === option.value,
                          )
                          return (
                            <label key={option.value} className="checkbox-item">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleOption(option)}
                                className="checkbox-input"
                              />
                              <span className="checkbox-label">
                                {option.label}
                              </span>
                            </label>
                          )
                        })}
                      </div>
                    ) : (
                      <div
                        style={{
                          textAlign: "left",
                          marginLeft: "10px",
                          width: "90%",
                        }}
                      >
                        {`${
                          type === "subfolderAssociation" &&
                          selectOptions.length === 0
                            ? "This practice does not have a subfolder. Please click 'Save' to grant the user access to the main practice."
                            : "No data found"
                        }`}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="checkbox-popup-footer popup-buttons">
                {saveLoading ? (
                  <span className="saving-text">Saving ...</span>
                ) : (
                  <>
                    {onlySubfolderAssociation && filteredOptions.length > 0 && (
                      <>
                        <label
                          className="checkbox-item"
                          style={{
                            marginRight: "50px",
                          }}
                        >
                          <span
                            title={`'Only Subfolders' allows the user to access only assigned subfolders within a practice. User cannot access faxes available in main practice. Use this for limited access control.`}
                          >
                            <Info
                              size={16}
                              style={{
                                marginRight: 4,
                                verticalAlign: "middle",
                                cursor: "help",
                              }}
                            />
                          </span>
                          <input
                            type="checkbox"
                            checked={onlySubfolderAssociationState}
                            onChange={() =>
                              setOnlySubfolderAssociationState(
                                !onlySubfolderAssociationState,
                              )
                            }
                            className="checkbox-input"
                          />
                          <span className="checkbox-label">
                            Only Subfolders
                          </span>
                        </label>
                      </>
                    )}

                    {(selectOptions.length !== 0 ||
                      type !== "subfolderAssociation") && (
                      <button
                        className="popup-button cancel"
                        type="button"
                        onClick={() => setOpen(false)}
                        style={{ marginRight: "10px" }}
                      >
                        Cancel
                      </button>
                    )}
                    {/* {filteredOptions.length > 0 && ( */}
                    <button
                      className="popup-button save"
                      onClick={applyChanges}
                    >
                      Save
                    </button>
                    {/* )} */}
                  </>
                )}
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  )
}
